import { DeleteObjectCommand, GetObjectCommand, PutObjectCommand } from '@aws-sdk/client-s3';
import moment from 'moment-timezone';
import sanitizeFilename from 'sanitize-filename';
import { handleError } from '../logger';
import { getS3Client, streamToBlob } from '../utilities/CommonUtilities';
import { ENVIRONMENT_VARIABLES } from '../constants/EnvironmentConstants';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();

export class EAHuddleService {
  private static readonly TEMPLATE_FILENAME = 'Meeting_Request_Template.docx';
  private static readonly MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  // TODO: revisit this value when finsuite backend is ready to accept images
  private static readonly MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  private static readonly ATTACHMENT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB for attachments

  /**
   * Uploads a file to S3 and returns the S3 URI
   * @param file - The file to upload
   * @param userId - User ID for organizing uploads
   * @returns Object containing success status, S3 URI, and other metadata
   */
  static async eaUploadToS3(
    file: File,
    userId: string
  ): Promise<{
    success: boolean;
    s3Url?: string;
    s3Key?: string;
    bucketName?: string;
    error?: string;
  }> {
    try {
      // Validate file size (max 2MB)
      if (file.size > this.MAX_FILE_SIZE) {
        return {
          success: false,
          error: `File size ${file.size} bytes exceeds ${this.MAX_FILE_SIZE} bytes limit (2MB)`
        };
      }

      // Get stage from environment variables
      // For personal stacks use add -${USER} at the end
      const bucketName = `das-finsuite-ea-uploads-${normalizedStage}`;

      // Generate unique S3 key with timestamp and sanitized filename
      const timestamp = moment().format('YYYYMMDDHHmmss');
      const sanitizedFilename = sanitizeFilename(file.name, { replacement: '_' });
      const s3Key = `ea-huddle-uploads/${userId}/${timestamp}/${sanitizedFilename}`;

      // Get S3 client with proper credentials
      const s3Client = getS3Client();

      if (!s3Client) {
        throw new Error('Unable to initialize S3 client. Please ensure you are logged in.');
      }

      // Convert file to ArrayBuffer for upload
      const arrayBuffer = await file.arrayBuffer();
      const buffer = new Uint8Array(arrayBuffer);

      // Upload file to S3
      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: s3Key,
        Body: buffer,
        ContentType: file.type
      });

      await s3Client.send(command);

      // Construct S3 URI
      const s3Url = `s3://${bucketName}/${s3Key}`;

      return {
        success: true,
        s3Url,
        s3Key,
        bucketName
      };
    } catch (error) {
      handleError('Error uploading file to S3', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload file to S3'
      };
    }
  }

  /**
   * Uploads a meeting attachment to S3 and returns the S3 URI.
   * Bucket: das-finsuite-ea-uploads-{stage}
   * Key format: {alias}/attachments/{meetingId}/{sanitizedFileName}
   *
   * @param file - The file to upload
   * @param alias - User alias (organizer)
   * @param meetingId - The meeting ID
   * @returns Object containing success status, S3 URI, and filename
   */
  static async uploadMeetingAttachment(
    file: File,
    alias: string,
    meetingId: string
  ): Promise<{
    success: boolean;
    s3Uri?: string;
    fileName?: string;
    error?: string;
  }> {
    try {
      if (file.size > this.ATTACHMENT_MAX_FILE_SIZE) {
        return {
          success: false,
          error: `File size exceeds 10MB limit`
        };
      }

      const bucketName = `das-finsuite-ea-uploads-${normalizedStage}`;

      const sanitizedName = sanitizeFilename(file.name, { replacement: '_' });
      const s3Key = `${alias}/attachments/${meetingId}/${sanitizedName}`;

      const s3Client = getS3Client();
      if (!s3Client) {
        throw new Error('Unable to initialize S3 client. Please ensure you are logged in.');
      }

      const arrayBuffer = await file.arrayBuffer();
      const buffer = new Uint8Array(arrayBuffer);

      await s3Client.send(
        new PutObjectCommand({
          Bucket: bucketName,
          Key: s3Key,
          Body: buffer,
          ContentType: file.type
        })
      );

      const s3Uri = `s3://${bucketName}/${s3Key}`;

      return {
        success: true,
        s3Uri,
        fileName: sanitizedName
      };
    } catch (error) {
      handleError('Error uploading meeting attachment to S3', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload attachment'
      };
    }
  }

  /**
   * Downloads a meeting attachment from S3 by its S3 URI.
   * Parses the URI to extract bucket/key, fetches the file, and triggers browser download.
   */
  static async downloadAttachment(s3Uri: string): Promise<void> {
    try {
      const s3Client = getS3Client();
      if (!s3Client) {
        throw new Error('Unable to initialize S3 client. Please ensure you are logged in.');
      }

      // Parse s3://bucket/key
      if (!s3Uri.startsWith('s3://')) {
        throw new Error(`Invalid S3 URI: ${s3Uri}`);
      }
      const withoutProtocol = s3Uri.substring(5);
      const slashIndex = withoutProtocol.indexOf('/');
      const bucket = withoutProtocol.substring(0, slashIndex);
      const key = withoutProtocol.substring(slashIndex + 1);
      const fileName = key.split('/').pop() ?? 'download';

      const response = await s3Client.send(
        new GetObjectCommand({
          Bucket: bucket,
          Key: key
        })
      );

      if (!response.Body) {
        throw new Error('Empty response from S3');
      }

      const blob = await streamToBlob(response.Body as ReadableStream, response.ContentType ?? 'application/octet-stream');

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      handleError('Error downloading attachment from S3', error);
      throw new Error('Failed to download attachment. Please try again.');
    }
  }

  /** Deletes a meeting attachment from S3 by its S3 URI. Fails silently. */
  static async deleteAttachment(s3Uri: string): Promise<void> {
    try {
      const s3Client = getS3Client();
      if (!s3Client || !s3Uri.startsWith('s3://')) return;
      const withoutProtocol = s3Uri.substring(5);
      const slashIndex = withoutProtocol.indexOf('/');
      await s3Client.send(
        new DeleteObjectCommand({
          Bucket: withoutProtocol.substring(0, slashIndex),
          Key: withoutProtocol.substring(slashIndex + 1)
        })
      );
    } catch (error) {
      handleError('Error deleting attachment from S3', error);
    }
  }
  /**
   * Downloads the EA Meeting Request Template from S3
   * Requires authenticated user with proper Cognito permissions
   */
  static async downloadTemplate(): Promise<void> {
    try {
      // Get stage from environment variables
      const bucketName = `ea-template-${normalizedStage}`;

      // Get S3 client with proper credentials (reusing existing utility)
      const s3Client = getS3Client();

      if (!s3Client) {
        throw new Error('Unable to initialize S3 client. Please ensure you are logged in.');
      }

      // Fetch the template from S3
      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: this.TEMPLATE_FILENAME
      });

      const response = await s3Client.send(command);

      if (!response.Body) {
        throw new Error('Empty response from S3');
      }

      // Convert the S3 response body to a Blob (reusing existing utility)
      const blob = await streamToBlob(response.Body as ReadableStream, this.MIME_TYPE);

      // Create download link and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.TEMPLATE_FILENAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      handleError('Error downloading template from S3', error);
      throw new Error('Failed to download template. Please try again or contact support.');
    }
  }
}
